<template>
  <base-wrapper :loading="loading || loadingJobStatus">
    <div class="space-y-4">
      <div class="flex justify-end gap-2">
        <base-input
          type="search"
          placeholder="Cari Stockist"
          :shadow="false"
          v-model="filter.search"
          debounce
          @native-input="loadOffices"
        />
        <base-button :loading="loadingExport.visible" @click="onExport">
          <Icon
            v-if="!loadingExport.visible"
            icon="heroicons:arrow-down-tray"
            class="h-4 w-4"
          />
          {{ loadingExport.visible ? loadingExport.text : 'Export' }}
        </base-button>
      </div>
      <datatable
        :columns="tableColumns"
        :total="summary.total"
        :perPage="offices.meta.page.perPage"
        :currentPage="currentPage"
        :meta="offices.meta"
        cursor
        @pagechanged="onPageChanged"
        @prev="currentPage--"
        @next="currentPage++"
      >
        <template #tbody="{ classes }">
          <tr
            v-for="(office, index) in offices.data"
            :key="index"
            :class="[classes.tr, 'bg-white hover:bg-green-100']"
          >
            <td
              :class="[classes.td, 'cursor-pointer space-y-1']"
              @click="onViewOffice(getOfficeIncluded(office))"
            >
              <p class="font-bold text-gray-900">
                {{ office.attributes.code }}
              </p>
              <p class="text-xs text-gray-700">{{ office.attributes.name }}</p>
            </td>
            <td
              :class="[classes.td, 'cursor-pointer']"
              @click="onViewOffice(getOfficeIncluded(office))"
            >
              {{ getOfficeIncluded(office).attributes.area_code }}
            </td>
            <td
              :class="[classes.td, 'cursor-pointer']"
              @click="onViewOffice(getOfficeIncluded(office))"
            >
              {{ getOfficeIncluded(office).attributes.phone ?? '-' }}
            </td>
            <td
              :class="[classes.td, 'cursor-pointer']"
              @click="onViewOffice(getOfficeIncluded(office))"
            >
              {{ getOfficeIncluded(office).attributes.cellphone ?? '-' }}
            </td>
            <td
              :class="[classes.td, 'cursor-pointer']"
              @click="onViewOffice(getOfficeIncluded(office))"
            >
              {{ getOfficeIncluded(office).attributes.full_address ?? '-' }}
            </td>
            <td :class="[classes.td, 'text-center']">
              <base-badge
                :class="
                  getOfficeIncluded(office).attributes.is_promoted
                    ? ''
                    : 'cursor-pointer'
                "
                :color="
                  getOfficeIncluded(office).attributes.is_promoted
                    ? 'indigo'
                    : 'green'
                "
                @click="onPromote(getOfficeIncluded(office))"
              >
                {{
                  getOfficeIncluded(office).attributes.is_promoted
                    ? getOfficeIncluded(office).attributes.promoted_by
                    : 'Promosi ke SC'
                }}
              </base-badge>
            </td>
            <td :class="[classes.td, 'text-center']">
              <base-badge
                :class="
                  getOfficeIncluded(office).attributes.is_pbs_activated
                    ? ''
                    : 'cursor-pointer'
                "
                :color="
                  getOfficeIncluded(office).attributes.is_pbs_activated
                    ? 'indigo'
                    : 'green'
                "
                @click="onActivatePbs(getOfficeIncluded(office))"
              >
                {{
                  getOfficeIncluded(office).attributes.is_pbs_activated
                    ? 'Aktif'
                    : 'Aktivasi P3S'
                }}
              </base-badge>
            </td>
          </tr>
        </template>
      </datatable>
    </div>

    <view-office-modal
      office-type="stockist"
      :visible="viewModal.visible"
      :office-id="viewModal.officeId"
      editable
      edit-route="master.stockist.edit"
      :columns="['origin_member', 'promoted_sc']"
      @close="viewModal.visible = false"
    />
    <promote-office-to-sc-modal
      :visible="promoteToScModal.visible"
      :office-id="promoteToScModal.officeId"
      @close="promoteToScModal.visible = false"
      @promoted="onPromoted"
    />
    <activate-office-pbs-modal
      :visible="activatePbsModal.visible"
      :office-id="activatePbsModal.officeId"
      @close="activatePbsModal.visible = false"
      @pbs-activated="onPbsActivated"
    />
  </base-wrapper>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import ViewOfficeModal from '@/components/office/view-office-modal.vue';
import PromoteOfficeToScModal from '@/components/office/promote-office-to-sc-modal.vue';
import ActivateOfficePbsModal from '@/components/office/activate-office-pbs-modal.vue';
import { mapActions, mapGetters } from 'vuex';
import { downloadFileUrl } from '../../../../../services/utils.service';

export default {
  mixins: [requestMixin],
  components: {
    PromoteOfficeToScModal,
    ViewOfficeModal,
    ActivateOfficePbsModal,
  },
  data() {
    return {
      currentPage: 1,
      filter: {
        search: null,
      },
      loading: false,
      loadingJobStatus: false,
      loadingExport: {
        visible: false,
        text: null,
      },
      offices: {
        meta: {
          page: {},
        },
        data: [],
      },
      promoteToScModal: {
        visible: false,
        officeId: null,
      },
      viewModal: {
        visible: false,
        officeId: null,
      },
      activatePbsModal: {
        visible: false,
        officeId: null,
      },
      summary: {},
    };
  },
  computed: {
    ...mapGetters({
      getChannelName: 'auth/getChannelName',
    }),
    tableColumns() {
      return [
        { id: 'code', name: 'Kode Stockist' },
        { id: 'area_code', name: 'Wilayah Harga' },
        { id: 'phone', name: 'No. Telp' },
        { id: 'cellphone', name: 'No. Whatsapp' },
        { id: 'full_address', name: 'Alamat' },
        { id: 'promotion_status', name: 'Status', theadClass: 'text-center' },
        { id: 'pbs_status', name: 'Status P3S', theadClass: 'text-center' },
      ];
    },
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    addListener() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      userChannel.bind(
        'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
        this.onExportProgress
      );
    },
    getOfficeIncluded(office) {
      return this.getSingleIncluded(
        this.offices,
        office.relationships.office.data.id
      );
    },
    async loadJobStatus() {
      this.loadingJobStatus = true;

      const [res, err] = await this.request(
        '/api/v1/users/-actions/check-job-status',
        {
          params: {
            type: 'export_pin_stockist',
          },
        }
      );

      if (!err) {
        const { running } = res;

        if (running) {
          this.startLoadingExport('Sedang Diproses');
        }
      }

      this.loadingJobStatus = false;
    },
    async loadOffices(params) {
      this.loading = true;

      const [res, err] = await this.request('/api/v1/offices', {
        params: {
          'filter[office_category_id]': 4,
          'filter[search]': this.filter.search,
          'page[limit]': 5,
          include: 'office',
          'fields[simple-offices]': 'code,name,office',
          'fields[offices]':
            'area_code,phone,cellphone,full_address,is_promoted,promoted_by,is_pbs_activated',
          'filter[with_summary]': true,
          ...params,
        },
      });

      if (!err) {
        this.offices = res;

        if (res.meta.summary) {
          this.summary = res.meta.summary;
        }

        if (!params) {
          this.currentPage = 1;
        }
      }

      this.loading = false;
    },
    onActivatePbs(office) {
      if (!office.attributes.is_pbs_activated) {
        this.activatePbsModal.officeId = office.id;
        this.activatePbsModal.visible = true;
      }
    },
    onPageChanged(page) {
      this.loadOffices({
        ...page,
        'filter[with_summary]': false,
      });
    },
    onPromote(office) {
      if (!office.attributes.is_promoted) {
        this.promoteToScModal.officeId = office.id;
        this.promoteToScModal.visible = true;
      }
    },
    onPromoted() {
      this.promoteToScModal.visible = false;

      this.loadOffices();
    },
    onPbsActivated() {
      this.activatePbsModal.visible = false;

      this.loadOffices();
    },
    onViewOffice(office) {
      this.viewModal.officeId = office.id;
      this.viewModal.visible = true;
    },
    async onExport() {
      this.startLoadingExport('Mengirim ke server');

      const [, err] = await this.request(
        `/api/v1/offices/-actions/export-pin`,
        {
          method: 'post',
          params: {
            office_type: 'stockist',
          },
        }
      );

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err),
        });

        this.stopLoadingExport();
      } else {
        this.startLoadingExport('Sedang diproses');
      }
    },
    onExportProgress(e) {
      if (e.type === 'App\\Notifications\\OfficePinExported') {
        if (e.status === 'failed') {
          this.stopLoadingExport();

          this.createAlert({
            data: 'Gagal mengexport stockist',
            status: 'error',
          });
        } else if (e.status === 'success') {
          this.stopLoadingExport();

          downloadFileUrl(e.file_url, 'stockist.csv', { blank: true });
        }
      }
    },
    removeListener() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      if (userChannel) {
        userChannel.unbind(
          'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
          this.onExportProgress
        );
      }
    },
    startLoadingExport(text) {
      this.loadingExport.text = text;
      this.loadingExport.visible = true;
    },
    stopLoadingExport() {
      this.loadingExport.visible = false;
    },
  },
  created() {
    this.loadJobStatus();
    this.loadOffices();
    this.addListener();
  },
  beforeRouteLeave(to, from, next) {
    this.removeListener();
    next();
  },
};
</script>
